import { Injectable } from '@angular/core';
import { Application } from 'app/applications/application.model';
import { ServiceType } from 'app/core/service-type.model';
import { OrderSettings } from 'app/orders/settings/settings.model';
import { LocalStorageService } from 'app/services/storage/local-storage.service';
import { Savable } from 'app/services/storage/savable';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith } from 'rxjs/operators';

export interface IUserSettingsService {
  serviceType: ServiceType;
  documentType: string;
  visibleInvoicesUploadArea: boolean;
  welcomeTraining: boolean;
  exportFormat: string;
  region: string;
  verificationType: string;
  rememberImportSettings: boolean;
  defaultApplications: { [id: string]: Application };
  defaultApplication: Application;
}

@Injectable()
@Savable.key('userSettings')
export class UserSettingsService extends Savable implements IUserSettingsService {

  @Savable.property
  serviceType: ServiceType;

  @Savable.property
  documentType: string;

  @Savable.property
  visibleInvoicesUploadArea: boolean;

  @Savable.property
  welcomeTraining: boolean;

  @Savable.property
  region: string;

  @Savable.property
  exportFormat: string;

  @Savable.property
  verificationType: string;

  @Savable.property
  rememberImportSettings: boolean;

  @Savable.property
  defaultApplications: { [serviceType: string]: Application };

  showBackground: boolean;

  serviceType$: Observable<ServiceType>;

  get defaultApplication(): Application {
    return this.defaultApplications[this.serviceType];
  }

  set defaultApplication(application: Application) {
    this.defaultApplications[this.serviceType] = application;
    this.save();
  }

  get orderSettings(): OrderSettings {
    return {
      region: this.region,
      exportFormat: this.exportFormat,
      verificationType: this.verificationType
    };
  }

  set orderSettings(orderSettings: OrderSettings) {
    this.region = orderSettings.region;
    this.exportFormat = orderSettings.exportFormat;
    this.verificationType = orderSettings.verificationType;
  }

  constructor(private _localStorageService: LocalStorageService) {
    super(_localStorageService);
    this.restore();
    this.serviceType$ = this._localStorageService.changeKey
      .pipe(
        filter(change => change.key === 'userSettings'),
        map(() => this.serviceType),
        startWith(this.serviceType),
        distinctUntilChanged(),
        shareReplay()
      );
  }

  protected restore(): IUserSettingsService {
    const restored: IUserSettingsService = super.restore();
    this.serviceType = restored.serviceType || ServiceType.InvoiceCapture;
    this.documentType = restored.documentType || 'invoice';
    this.visibleInvoicesUploadArea = Savable.boolean(restored.visibleInvoicesUploadArea, true);
    this.welcomeTraining = Savable.boolean(restored.welcomeTraining, false);
    this.region = restored.region || 'US';
    this.exportFormat = restored.exportFormat || (<any>restored).format || 'Xml';
    this.verificationType = restored.verificationType || (<any>restored).verification || 'Troubleshooting';
    this.rememberImportSettings = restored.rememberImportSettings || false;
    this.defaultApplications = restored.defaultApplications || { 'ic': null, 'dc': null };
    return this;
  }
}
